import { TransactionError } from '@mercurial-finance/optimist';
import bs58 from 'bs58';
import {
  AccountKeysFromLookups,
  Blockhash,
  ConfirmedTransactionMeta,
  PublicKey,
  Signer,
  Transaction,
  TransactionSignature,
  VersionedMessage,
  VersionedTransaction,
  VersionedTransactionResponse,
} from '@solana/web3.js';
import { JUPITER_COMMON_ERRORS, JUPITER_PROGRAM_V6_ID, WRAPPED_SOL_MINT } from '../constants';
import { getEvents } from './getEvents';
import { BN, Event } from '@coral-xyz/anchor';

/** Should this be a method in web3.js? */
function getKeyMap(message: VersionedMessage, accountKeysFromLookups: AccountKeysFromLookups | undefined) {
  return message
    .getAccountKeys({ accountKeysFromLookups })
    .keySegments()
    .flat()
    .reduce((acc, key, index) => {
      acc.set(key.toBase58(), index);
      return acc;
    }, new Map<string, number>());
}

function diffTokenBalance(meta: ConfirmedTransactionMeta, owner: string, mint: string): number | undefined {
  const postBalance = meta.postTokenBalances?.find(
    (postTokenBalance) => postTokenBalance.owner === owner && postTokenBalance.mint === mint,
  )?.uiTokenAmount.amount;
  const preBalance = meta.preTokenBalances?.find(
    (preTokenBalance) => preTokenBalance.owner === owner && preTokenBalance.mint === mint,
  )?.uiTokenAmount.amount;

  if (!postBalance) return;
  // When token account is created it isn't present in preBalance
  return Math.abs(parseInt(postBalance) - (preBalance !== undefined ? parseInt(preBalance) : 0));
}

export function extractTokenBalanceChangeFromTransaction(
  meta: ConfirmedTransactionMeta,
  user: PublicKey,
  mint: PublicKey,
): number | undefined {
  if (!meta) return;
  return diffTokenBalance(meta, user.toBase58(), mint.toBase58());
}

/** Assumption: user is the signer so it can never be rolled */
export function extractSOLChangeFromTransaction(
  meta: ConfirmedTransactionMeta,
  keyMap: Map<string, number>,
  user: PublicKey,
): number {
  const accountKeyIndex = keyMap.get(user.toBase58());
  if (accountKeyIndex !== undefined) {
    return Math.abs(meta.postBalances[accountKeyIndex] - meta.preBalances[accountKeyIndex]);
  }

  return 0;
}

// Event might be missing from log truncated
export function extractTokenBalanceChangesFromEvents(
  events: Event[],
  inputMint: PublicKey,
  outputMint: PublicKey,
): [number, number] {
  let inputAmount = new BN(0);
  let outputAmount = new BN(0);

  events.forEach((event: any) => {
    if (event.data.inputMint.equals(inputMint)) {
      inputAmount = inputAmount.add(event.data.inputAmount);
    }
    if (event.data.outputMint.equals(outputMint)) {
      outputAmount = outputAmount.add(event.data.outputAmount);
    }
  });

  return [Number(inputAmount.toString()), Number(outputAmount.toString())];
}

export function getTokenBalanceChangesFromTransactionResponse({
  txid,
  user,
  inputMint,
  outputMint,
  transactionResponse,
  hasWrappedSOL,
}: {
  txid: TransactionSignature;
  user: PublicKey;
  inputMint: PublicKey;
  outputMint: PublicKey;
  transactionResponse: VersionedTransactionResponse | null;
  hasWrappedSOL: boolean;
}): [number, number] {
  let sourceTokenBalanceChange: number | undefined;
  let destinationTokenBalanceChange: number | undefined;
  let events: Event[] = [];
  let swapEvents: Event[] = [];

  if (transactionResponse && transactionResponse.meta) {
    let { meta, transaction } = transactionResponse;
    events = getEvents(transactionResponse);
    swapEvents = events.filter((event) => event.name === 'SwapEvent');
    [sourceTokenBalanceChange, destinationTokenBalanceChange] = extractTokenBalanceChangesFromEvents(
      swapEvents,
      inputMint,
      outputMint,
    );

    // If we don't get the balance changes from the events, we extract them the old fashion way.
    if (!sourceTokenBalanceChange || !destinationTokenBalanceChange) {
      const keyMap = getKeyMap(transaction.message, meta.loadedAddresses);

      sourceTokenBalanceChange =
        inputMint.equals(WRAPPED_SOL_MINT) && !hasWrappedSOL
          ? extractSOLChangeFromTransaction(meta, keyMap, user)
          : extractTokenBalanceChangeFromTransaction(meta, user, inputMint);
      destinationTokenBalanceChange =
        outputMint.equals(WRAPPED_SOL_MINT) && !hasWrappedSOL
          ? extractSOLChangeFromTransaction(meta, keyMap, user)
          : extractTokenBalanceChangeFromTransaction(meta, user, outputMint);
    }
  }

  if (!(sourceTokenBalanceChange && destinationTokenBalanceChange)) {
    throw new TransactionError(
      'Cannot find source or destination token account balance change',
      txid,
      JUPITER_COMMON_ERRORS['BalancesNotExtractedProperly'].code,
      [JUPITER_PROGRAM_V6_ID.toBase58()],
    );
  }

  return [sourceTokenBalanceChange, destinationTokenBalanceChange];
}

export function getUnixTs() {
  return new Date().getTime();
}

export function getSignature(transaction: Transaction | VersionedTransaction): string {
  const signature = 'signature' in transaction ? transaction.signature : transaction.signatures[0];
  if (!signature) {
    throw new Error('Missing transaction signature, the transaction was not signed by the fee payer');
  }
  return bs58.encode(signature);
}

export function getTransactionBlockhash(transaction: Transaction | VersionedTransaction): Blockhash {
  if ('version' in transaction) {
    return transaction.message.recentBlockhash;
  } else {
    if (!transaction.recentBlockhash) throw new Error('Missing blockhash');
    return transaction.recentBlockhash;
  }
}

export function signTransaction(
  transaction: Transaction | VersionedTransaction,
  signers: Signer[],
): TransactionSignature {
  if ('version' in transaction) {
    transaction.sign(signers);
  } else {
    transaction.sign(...signers);
  }
  return getSignature(transaction);
}
